import { useState } from 'react'
import { Link } from 'react-router-dom'
import { designApiKey } from '../lib/elevenLabsKey'
import './Admin.css'

const DEFAULT_VOICE_ID = 'ZF6FPAbjXT4488VcRRnw'
const DEFAULT_MODEL_ID = 'eleven_v3'
const VOICE_TAG = '[excited]'
const LETTERS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))

type FileSystemWritableFileStream = {
  write: (data: BufferSource | Blob | string) => Promise<void>
  close: () => Promise<void>
}

type FileSystemFileHandle = {
  createWritable: () => Promise<FileSystemWritableFileStream>
}

type FileSystemDirectoryHandle = {
  getFileHandle: (
    name: string,
    options?: { create?: boolean },
  ) => Promise<FileSystemFileHandle>
}

type PickerWindow = Window & {
  showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>
}

type Status =
  | { kind: 'idle' }
  | { kind: 'running'; done: number; total: number; current: string }
  | { kind: 'done'; count: number; folderName: string | null }
  | { kind: 'error'; message: string }

async function ttsLetter(opts: {
  apiKey: string
  voiceId: string
  modelId: string
  text: string
}): Promise<Blob> {
  const url = `https://api.elevenlabs.io/v1/text-to-speech/${encodeURIComponent(
    opts.voiceId,
  )}?output_format=mp3_44100_128`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'xi-api-key': opts.apiKey,
      'Content-Type': 'application/json',
      Accept: 'audio/mpeg',
    },
    body: JSON.stringify({ text: opts.text, model_id: opts.modelId }),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`ElevenLabs ${res.status}: ${body.slice(0, 200) || res.statusText}`)
  }
  return res.blob()
}

async function writeBlobToDirectory(
  dir: FileSystemDirectoryHandle,
  name: string,
  blob: Blob,
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true })
  const writable = await handle.createWritable()
  await writable.write(blob)
  await writable.close()
}

function downloadBlob(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export default function AdminToolGenerateAlphabet() {
  const apiKey = designApiKey((import.meta.env.VITE_ELEVENLABS_API_KEY ?? '').trim())
  const [voiceId, setVoiceId] = useState(DEFAULT_VOICE_ID)
  const [modelId, setModelId] = useState(DEFAULT_MODEL_ID)
  const [status, setStatus] = useState<Status>({ kind: 'idle' })

  const pickerSupported = typeof (window as PickerWindow).showDirectoryPicker === 'function'

  const handleGenerate = async () => {
    if (!apiKey) {
      setStatus({ kind: 'error', message: 'VITE_ELEVENLABS_API_KEY is not set in .env.local.' })
      return
    }

    let dir: FileSystemDirectoryHandle | null = null
    if (pickerSupported) {
      try {
        const picker = (window as PickerWindow).showDirectoryPicker
        dir = (await picker!({ mode: 'readwrite' })) ?? null
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        console.error('[admin-tools] showDirectoryPicker failed', err)
        const message = err instanceof Error ? err.message : 'Folder selection failed'
        setStatus({ kind: 'error', message })
        return
      }
    }

    setStatus({ kind: 'running', done: 0, total: LETTERS.length, current: LETTERS[0] })
    try {
      for (let i = 0; i < LETTERS.length; i++) {
        const letter = LETTERS[i]
        setStatus({ kind: 'running', done: i, total: LETTERS.length, current: letter })
        const blob = await ttsLetter({
          apiKey,
          voiceId: voiceId.trim() || DEFAULT_VOICE_ID,
          modelId: modelId.trim() || DEFAULT_MODEL_ID,
          text: `${VOICE_TAG} ${letter}`,
        })
        const filename = `${letter}.mp3`
        if (dir) {
          await writeBlobToDirectory(dir, filename, blob)
        } else {
          downloadBlob(filename, blob)
        }
      }
      setStatus({ kind: 'done', count: LETTERS.length, folderName: dir ? 'the chosen folder' : null })
    } catch (err) {
      console.error('[admin-tools] generate alphabet failed', err)
      const message = err instanceof Error ? err.message : 'Generation failed'
      setStatus({ kind: 'error', message })
    }
  }

  const running = status.kind === 'running'

  return (
    <section className="admin">
      <Link to="/admin-tools" className="detail-back">
        ← Admin Tools
      </Link>

      <h1 className="page-title">Generate Alphabet</h1>
      <p className="page-lede">
        Generates 26 mp3 files (A–Z) via the ElevenLabs TTS API at 128 kbps / 44.1 kHz
        {pickerSupported
          ? ' and writes them to a folder you choose.'
          : ". Your browser doesn't support the folder picker, so the files will be downloaded individually."}
      </p>

      {!apiKey && (
        <p className="admin__error" role="alert">
          VITE_ELEVENLABS_API_KEY is not set in .env.local. Dev-only tool — do not set it in production.
        </p>
      )}

      <div className="card">
        <label style={{ display: 'block', marginBottom: '0.75rem' }}>
          <div>Voice ID</div>
          <input
            type="text"
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            style={{ width: '100%', fontFamily: 'var(--mono, monospace)' }}
            disabled={running}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '1rem' }}>
          <div>Model ID</div>
          <input
            type="text"
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            style={{ width: '100%', fontFamily: 'var(--mono, monospace)' }}
            disabled={running}
          />
        </label>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleGenerate}
          disabled={running || !apiKey}
        >
          {running
            ? `Generating ${status.current} (${status.done}/${status.total})…`
            : 'Generate A–Z'}
        </button>
      </div>

      {status.kind === 'running' && (
        <p>
          Generating {status.current} — {status.done} of {status.total} done.
        </p>
      )}
      {status.kind === 'done' && (
        <p>
          Generated {status.count} files
          {status.folderName ? ` into ${status.folderName}` : ' (downloaded individually)'}.
        </p>
      )}
      {status.kind === 'error' && (
        <p className="admin__error" role="alert">
          {status.message}
        </p>
      )}
    </section>
  )
}
